// @flow

import * as React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Box } from 'react-system';
import { useLocale } from '../../hooks/locale.js';
import { useRouter } from '../../controls/router';
import { ChatSteps } from '../../controls/ChatForm';
import { Center } from '../../controls/Center';
import { DevelopmentFirstBuyerStep } from './DevelopmentFirstBuyerStep.js';
import { DevelopmentSecondBuyerStep } from './DevelopmentSecondBuyerStep.js';
import { DevelopmentVerificationStep } from './development-verification-step.js';
import { DevelopmentConfirmationStep } from './DevelopmentConfirmationStep.js';
import type {
  DevelopmentRegistrationQuery,
  DevelopmentRegistrationStep,
} from './__generated__/DevelopmentRegistrationQuery.graphql';

export const getStepNumber = (step: ?DevelopmentRegistrationStep): string => {
  switch (step) {
    case 'first_buyer':
      return '1';
    case 'second_buyer':
      return '2';
    case 'verification':
      return '3';
    case 'result':
      return '4';
    default:
      return '1';
  }
};

const BaseDevelopmentRegistration = () => {
  const router = useRouter();
  const id = router.query.id;
  const data = useLazyLoadQuery<DevelopmentRegistrationQuery>(
    graphql`
      query DevelopmentRegistrationQuery($id: ID!, $skip: Boolean!) {
        ...DevelopmentFirstBuyerStep_root
        ...DevelopmentSecondBuyerStep_root
        ...developmentVerificationStep_root
        developmentRegistrationForm: node(id: $id) @skip(if: $skip) {
          ... on DevelopmentRegistrationForm {
            ...DevelopmentFirstBuyerStep_form
            ...DevelopmentSecondBuyerStep_form
            ...developmentVerificationStep_form
            ...DevelopmentConfirmationStep_form
            id
            step
            developmentProduct {
              slug
            }
            numberOfBuyers
          }
        }
      }
    `,
    { id: id ?? '', skip: id == null },
  );
  const form = data.developmentRegistrationForm;
  const formId = form?.id;

  const { t } = useLocale();
  const step = router.query.step || getStepNumber('first_buyer');

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  if (form == null || formId == null) {
    return null;
  }

  const currentStep = Number.parseFloat(step);
  const maxStep = Number.parseFloat(getStepNumber(form.step));

  // invalid step is passed
  if (Number.isNaN(currentStep)) {
    return null;
  }

  const pathname = '/development-registration';

  const steps: Array<{|
    href: {| +pathname: string, +query?: { [string]: string, ... } |},
    index: number,
    label: string,
  |}> = [
    {
      index: 0,
      href: {
        pathname: '/development',
        query: {
          slug: form.developmentProduct?.slug ?? '',
        },
      },
      label: t('back'),
    },
    {
      index: 1,
      href: {
        pathname,
        query: { step: getStepNumber('first_buyer'), id: formId },
      },
      label: t('firstBuyer'),
    },
    form.numberOfBuyers === 2
      ? {
          index: 2,
          href: {
            pathname,
            query: { step: getStepNumber('second_buyer'), id: formId },
          },
          label: t('secondBuyer'),
        }
      : null,
    {
      index: 3,
      href: {
        pathname,
        query: { step: getStepNumber('verification'), id: formId },
      },
      label: t('verification'),
    },
    {
      index: 4,
      href: {
        pathname,
        query: { step: getStepNumber('result'), id: formId },
      },
      label: t('result'),
    },
  ].filter(Boolean);

  return (
    <Box css={{ minHeight: '100vh', background: '#fff' }}>
      <Center>
        <ChatSteps
          steps={steps}
          currentStep={steps.findIndex(s => currentStep === s.index) + 1}
          maxStep={steps.findIndex(s => maxStep === s.index) + 1}
        />
        <Box mx="auto" pt={3} pb={5} css={{ maxWidth: 460 }}>
          {step === '1' && (
            <DevelopmentFirstBuyerStep root={data} form={form} />
          )}
          {step === '2' && (
            <DevelopmentSecondBuyerStep root={data} form={form} />
          )}
          {step === '3' && (
            <DevelopmentVerificationStep root={data} form={form} />
          )}
          {step === '4' && <DevelopmentConfirmationStep form={form} />}
        </Box>
      </Center>
    </Box>
  );
};

export const DevelopmentRegistration = (): React.Node => {
  return (
    <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
      <BaseDevelopmentRegistration />
    </React.Suspense>
  );
};
